import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { FarmRiskType, lookupsJson } from '@ourbranch/lookups';

import Field from 'core/components/form/form.v2';
import withDatePicker from 'core/components/with-date-picker';
import {
  getMaskedRiskTypeOptions,
  resetFarmRiskBuildingOnTypeChange,
  clearFarmRiskBuildingAndCoverages
} from 'common/helpers/farm-helpers';

import FO3RiskDetail from './FO3-risk-detail';
import FO4RiskDetail from './FO4-risk-detail';
import FO6RiskDetail from './FO6-risk-detail';

const baseRiskTypeOptions = lookupsJson.farm.riskType;

const InitialRiskDetail = () => {
  const { values, setFieldValue } = useFormikContext();

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;

  // Filter+relabel risk type options by policy type mask. For Forreston Rental (Y), drops
  // FO-4 / Scheduled / Unscheduled Farm Property and relabels FO-6 → "Other Structures (FO-6)". (#31367)
  // Resolve the mask from the primary LOCATION's state, not the applicant/mailing state — an
  // out-of-state mailing address otherwise finds no mask and leaks the farm-only risk types onto
  // Home/Rental (#32094). Fall back to the mailing state before a location address is entered.
  const riskMaskState = farmLocation?.address?.state || values.state;
  const riskTypeOptions = getMaskedRiskTypeOptions(values.policyType, riskMaskState, baseRiskTypeOptions);

  // Clear the selected risk type only when it's no longer one of the currently offered options.
  // The options depend on BOTH the policy type (#32225) and the state-specific mask (#32094), so the
  // effect intentionally re-runs on either `values.policyType` or `riskMaskState`. The guard
  // (`!riskTypeOptions.some(...)`) means a still-valid selection is never wiped — only a selection
  // that the new policy type OR the new location state has made invalid is cleared. (#32273)
  useEffect(() => {
    if (farmRisk?.type && riskTypeOptions.length > 0 && !riskTypeOptions.some((opt) => opt.id === farmRisk.type)) {
      setFieldValue(`${farmRiskPath}.type`, '');
      setFieldValue(`${farmRiskPath}.description`, '');
      // #32514 reopen: also drop the now-orphaned building/coverages. This effect blanks `type` to
      // '' before the user re-picks, so the onChange cross-family reset can no longer resolve a
      // `fromType` family on the next selection — leaving the previous type's fields (e.g. an FO-6
      // coverageF) to flow into the replacement type's rater and reproduce the "farm rating error".
      clearFarmRiskBuildingAndCoverages({ riskPath: farmRiskPath, setFieldValue });
    }
  }, [values.policyType, riskMaskState]);

  return (
    <Grid container spacing={2}>
      <Field
        xs={6}
        type="select"
        id="farmRiskType"
        name={`${farmRiskPath}.type`}
        mode="light"
        label="Risk Type*"
        options={riskTypeOptions}
        fast={false}
        onChange={(value) => {
          // #32514 reopen: switching the primary structure across form families (e.g. FO-6 "other
          // structures" → FO-3 dwelling) leaves the previous type's building + coverages in place.
          // Those incompatible fields flow into the dwelling rater and fail with a "farm rating
          // error" (an agent hit this after entering an FO-6, getting the missing-dwelling error,
          // then switching to FO-3). Reset the stale data on any cross-family switch so the new
          // type's form re-collects the right details. Same-family switches (FO-1 ↔ FO-3) are a no-op.
          resetFarmRiskBuildingOnTypeChange({
            fromType: farmRisk?.type,
            toType: value,
            riskPath: farmRiskPath,
            setFieldValue
          });
          if (value === FarmRiskType.Fo6Scheduled) {
            setFieldValue(`${farmRiskPath}.description`, 'FO6 Scheduled');
          } else if (value === FarmRiskType.Fo6Unscheduled) {
            setFieldValue(`${farmRiskPath}.description`, 'FO6 Unscheduled');
          } else if (farmRisk?.type === FarmRiskType.Fo6Scheduled || farmRisk?.type === FarmRiskType.Fo6Unscheduled) {
            // Clear description when switching FROM auto-description types to user-editable types
            setFieldValue(`${farmRiskPath}.description`, '');
          }
        }}
      />
      <Field
        xs={6}
        type="string"
        id="farmRisk.description"
        name={`${farmRiskPath}.description`}
        mode="light"
        label="Risk Name"
        disabled={farmRisk?.type === FarmRiskType.Fo6Scheduled || farmRisk?.type === FarmRiskType.Fo6Unscheduled}
      />

      {[FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(farmRisk?.type) && <FO3RiskDetail />}

      {farmRisk?.type === FarmRiskType.Fo4 && <FO4RiskDetail />}

      {farmRisk?.type === FarmRiskType.Fo6Structures && <FO6RiskDetail />}
    </Grid>
  );
};

export default withDatePicker(InitialRiskDetail);
